import { colorVisionInstruction } from "@constants/instructionConstants";
import { virtualAssistanceColorVisionConfig } from "@constants/virtualAssistanceConstants";
import {
  AvailablePreTestConfigurationOptions,
  TestType,
  EyeType,
  TestConfiguration,
  TestCategory,
  TestLanguage,
  WaitDuration,
  TestUIConfiguration,
  ColorVisionPlateNumber,
} from "types/testTypes";
import {
  AVAILABLE_EYE_OPTIONS_BINOCULAR_MONOCULAR,
  AVAILABLE_VIRTUAL_ASSISTANT_OPTIONS,
  DUMMY_AVAILABLE_TEST_SPECIFIC_CONFIGURATION_OPTIONS,
} from "../configurationHelperConstants";
import { OSS_TUTORIAL_SUPPORTED_LANGUAGES } from "@constants/languageConstants";
import {
  CATEGORY_VERSION_MAP,
  FEATURE_VERSION_MAP,
  TEST_TYPE_VERSION_MAP,
} from "../versionConstants";
import color_vision_plates_en from "@data/color_vision_plates.json";

export const availableColorVisionTestConfigurationOptions: AvailablePreTestConfigurationOptions = {
  testTypes: {
    configurationFieldName: "testType",
    validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.ColorVision],
    choices: [
      {
        validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.ColorVision],
        value: TestType.ColorVision,
      },
      {
        validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.ColorVisionAutomatic],
        value: TestType.ColorVisionAutomatic,
      },
    ],
    isMandatory: true,
  },
  eyeConfigurationOptions: {
    ...{
      ...AVAILABLE_EYE_OPTIONS_BINOCULAR_MONOCULAR,
      eyeTypes: {
        ...AVAILABLE_EYE_OPTIONS_BINOCULAR_MONOCULAR.eyeTypes,
        choices: [
          { validSinceVersion: null, value: EyeType.BothEye },
          {
            validSinceVersion: FEATURE_VERSION_MAP.colorVisionSingleEyeTestSupport,
            value: EyeType.RightEye,
          },
          {
            validSinceVersion: FEATURE_VERSION_MAP.colorVisionSingleEyeTestSupport,
            value: EyeType.LeftEye,
          },
        ],
      },
    },
  },
  languageOptions: {
    choices: OSS_TUTORIAL_SUPPORTED_LANGUAGES,
    validSinceVersion: FEATURE_VERSION_MAP.tutorialMode,
    configurationFieldName: "testLanguage",
  },
  virtualAssistantOptions: AVAILABLE_VIRTUAL_ASSISTANT_OPTIONS,
  testSpeedOptions: null,
  testSpecificConfigurationOptions: {
    ...DUMMY_AVAILABLE_TEST_SPECIFIC_CONFIGURATION_OPTIONS,
    plateNumberOptions: {
      configurationFieldName: "plateCount",
      choices: [
        {
          validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.ColorVisionAutomatic],
          value: ColorVisionPlateNumber.MediumPlateCount,
        },
        {
          validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.ColorVisionAutomatic],
          value: ColorVisionPlateNumber.SmallPlateCount,
        },
        {
          validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.ColorVisionAutomatic],
          value: ColorVisionPlateNumber.LargePlateCount,
        },
      ],
      validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.ColorVisionAutomatic],
      isMandatory: true,
    },
    toggleShowTutorial: {
      configurationFieldName: "showTutorial",
      choices: null,
      validSinceVersion: FEATURE_VERSION_MAP.tutorialMode,
    },
  },
};

export const colorVisionTestConfiguration: TestConfiguration = {
  name: TestCategory.ColorVision,
  testType: TestType.ColorVision,
  eyeConfiguration: {
    eyeType: EyeType.BothEye,
    isCalibrationNeeded: false,
    autoPauseForGaze: false,
    isTwoScreenFixationEnabled: true,
    isBackToBackEyeTest: false,
  },
  testSpeed: WaitDuration.Standard,
  testLanguage: TestLanguage.English,
  virtualAssistanceConfig: virtualAssistanceColorVisionConfig,
  isPressToStartEnabled: false,
  extraData: {
    plateCount: ColorVisionPlateNumber.LargePlateCount,
    questionsJson: color_vision_plates_en,
    showTutorial: false,
  },
};

export const colorVisionUIConfiguration: TestUIConfiguration = {
  showProgressBar: true,
  instructions: colorVisionInstruction,
};

export type ColorVisionLineSpecificAnswer = "line_1" | "line_2";
export type ColorVisionSymbolicAnswer =
  | "nothing"
  | "2_lines"
  | "no_lines"
  | "1_line"
  | "trace_line"
  | ColorVisionLineSpecificAnswer
  | "other_number"
  | "not_sure";
export type ColorVisionAnswerValue = number | ColorVisionSymbolicAnswer | string;

export interface ColorVisionPlatePrompt {
  questionKey: string;
  quickAnswers: ColorVisionAnswerValue[];
}

const OPERATOR_QUESTION_KEY_MAP: Record<string, string> = {
  colorVisionPlateQuestionNumber: "colorVisionQuestionNumber",
  colorVisionPlateQuestionAnyNumber: "colorVisionQuestionAnyNumber",
  colorVisionPlateQuestionHowManyLines: "colorVisionQuestionTraceHowManyLines",
  colorVisionPlateQuestionTraceLine: "colorVisionQuestionAnyTraceableLine",
};

export const OPTION_KEY_ANSWER_MAP: Record<string, ColorVisionSymbolicAnswer> = {
  colorVisionOptionNoNumber: "nothing",
  colorVisionOption1Line: "1_line",
  colorVisionOption2Lines: "2_lines",
  colorVisionOptionNoLines: "no_lines",
};

export function toColorVisionAnswerValue(option: string | number): ColorVisionAnswerValue {
  return typeof option === "number" ? option : (OPTION_KEY_ANSWER_MAP[option] ?? option);
}

export const colorVisionValues: { [key: number]: number | string } = Object.fromEntries(
  color_vision_plates_en.map((plate) => [plate.plate, toColorVisionAnswerValue(plate.correct)]),
);

export const colorVisionPlatePrompts: Record<number, ColorVisionPlatePrompt> = Object.fromEntries(
  color_vision_plates_en.map((plate) => [
    plate.plate,
    {
      questionKey: OPERATOR_QUESTION_KEY_MAP[plate.description],
      quickAnswers: plate.options.map(toColorVisionAnswerValue),
    },
  ]),
);

export const colorVisionSubtypeInferenceMap: Record<
  number,
  { protanAnswer: ColorVisionAnswerValue; deutanAnswer: ColorVisionAnswerValue }
> = {
  22: { protanAnswer: 6, deutanAnswer: 2 },
  23: { protanAnswer: 2, deutanAnswer: 4 },
  24: { protanAnswer: 5, deutanAnswer: 3 },
  // NOTE: Unity content currently has plate-25/26 order shifted vs canonical Ishihara sequencing.
  // In this project the numeric diagnostic choice (96 / 6 / 9) is collected on plate 26, not 25.
  // Keep this mapping unless Unity plate ordering is corrected.
  26: { protanAnswer: 6, deutanAnswer: 9 },
};
